/**
 * O plano aberto: as ações, o prazo de cada uma e o que ainda falta (issue #359).
 *
 * A tarefa daqui é acompanhar — ver o que venceu, atualizar situação e concluir com evidência.
 * Por isso "Nova ação" é a única ação primária, e trazer o catálogo fica ao lado, em segundo
 * plano: ele é o caminho da primeira vez, e o vazio da tabela já o oferece.
 *
 * A data de elaboração fica no cabeçalho porque é dela que sai todo prazo desta tela.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LibraryBooks as CatalogoIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import {
  ActionButton,
  DataTable,
  InlineFilterBar,
  InlineSelect,
  InlineToggles,
  PageShell,
  type DataTableColumn,
} from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import { useFilterState } from '../../hooks/useFilterState';
import { actionPlanService } from '../../services/actionPlanService';
import {
  COR_DA_PRIORIDADE,
  COR_DA_SITUACAO,
  FILTROS_DE_ITENS_VAZIOS,
  PRIORIDADES,
  RESPONSAVEIS,
  ROTULO_DA_PRIORIDADE,
  ROTULO_DA_SITUACAO,
  ROTULO_DA_SITUACAO_DO_PLANO,
  ROTULO_DO_RESPONSAVEL,
  ROTULO_DO_TIPO_DE_ACAO,
  SITUACOES_DA_ACAO,
  formatarDia,
  situacaoDoPrazo,
  type ActionPlan,
  type ActionPlanItem,
  type DadosDaAcao,
  type FiltrosDeItens,
} from '../../types/actionPlan';
import { ActionPlanItemDialog } from './components/ActionPlanItemDialog';
import { CatalogImportDialog } from './components/CatalogImportDialog';
import { DueDateBadge } from './components/DueDateBadge';

const colunas: DataTableColumn<ActionPlanItem>[] = [
  {
    id: 'norma',
    label: 'Norma / origem',
    width: 160,
    truncate: true,
    render: (i) =>
      i.originType === 'DENUNCIA' ? (
        <Chip size="small" color="warning" variant="outlined" label={i.norma ?? 'Denúncia'} />
      ) : (
        i.norma ?? '—'
      ),
  },
  {
    id: 'description',
    label: 'Ação',
    primary: true,
    width: '32%',
    truncate: true,
    render: (i) => i.description,
  },
  {
    id: 'actionType',
    label: 'Tipo',
    render: (i) => ROTULO_DO_TIPO_DE_ACAO[i.actionType],
  },
  {
    id: 'priority',
    label: 'Prioridade',
    render: (i) => (
      <Chip
        size="small"
        variant="outlined"
        color={COR_DA_PRIORIDADE[i.priority]}
        label={ROTULO_DA_PRIORIDADE[i.priority]}
      />
    ),
  },
  {
    id: 'dueDate',
    label: 'Prazo',
    render: (i) => <DueDateBadge dueDate={i.dueDate} status={i.status} />,
  },
  {
    id: 'responsible',
    label: 'Responsável',
    render: (i) => i.responsibleName ?? ROTULO_DO_RESPONSAVEL[i.responsible],
  },
  {
    id: 'status',
    label: 'Situação',
    render: (i) => (
      <Chip size="small" color={COR_DA_SITUACAO[i.status]} label={ROTULO_DA_SITUACAO[i.status]} />
    ),
  },
];

function emAcoes(quantidade: number): string {
  return `${quantidade} ${quantidade === 1 ? 'ação' : 'ações'}`;
}

export const ActionPlanDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const criar = useAcao('action_plan.create', 'Nova ação');
  const editar = useAcao('action_plan.update', 'Editar ação');
  const excluir = useAcao('action_plan.delete', 'Excluir ação');

  const [plano, setPlano] = useState<ActionPlan | null>(null);
  const [itens, setItens] = useState<ActionPlanItem[]>([]);
  /** Sem filtro nenhum: é o que sustenta as contagens do cabeçalho e as normas do filtro. */
  const [todasAsAcoes, setTodasAsAcoes] = useState<ActionPlanItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosDeItens>(FILTROS_DE_ITENS_VAZIOS);

  const [emEdicao, setEmEdicao] = useState<ActionPlanItem | null>(null);
  const [dialogoDaAcao, setDialogoDaAcao] = useState(false);
  const [dialogoDoCatalogo, setDialogoDoCatalogo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [aExcluir, setAExcluir] = useState<ActionPlanItem | null>(null);

  const { filters, setFilter, clearAll, activeChips, hasActiveFilters } = useFilterState(
    FILTROS_DE_ITENS_VAZIOS,
    {
      onFilter: setFiltros,
      live: true,
      labels: {
        status: 'Situação',
        priority: 'Prioridade',
        norma: 'Norma',
        responsible: 'Responsável',
        prazo: 'Prazo',
      },
      formatValue: {
        status: (v: string) => ROTULO_DA_SITUACAO[v as ActionPlanItem['status']],
        priority: (v: string) => ROTULO_DA_PRIORIDADE[v as ActionPlanItem['priority']],
        responsible: (v: string) => ROTULO_DO_RESPONSAVEL[v as ActionPlanItem['responsible']],
        prazo: (v: string) => (v === 'vencidas' ? 'Vencidas' : 'A vencer'),
      },
    }
  );

  const carregarPlano = useCallback(async () => {
    if (id === '') return;
    try {
      const [aberto, acoes] = await Promise.all([
        actionPlanService.obterPlano(id),
        actionPlanService.listarItens(id),
      ]);
      setPlano(aberto);
      setTodasAsAcoes(acoes);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível abrir o plano de ação');
    }
  }, [id]);

  const carregarItens = useCallback(async () => {
    if (id === '') return;
    setCarregando(true);
    try {
      setItens(await actionPlanService.listarItens(id, filtros));
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar as ações');
    } finally {
      setCarregando(false);
    }
  }, [filtros, id]);

  useEffect(() => {
    void carregarPlano();
  }, [carregarPlano]);

  useEffect(() => {
    void carregarItens();
  }, [carregarItens]);

  /** As normas oferecidas são as que este plano usa — filtro que devolve zero não ajuda ninguém. */
  const normasDoPlano = useMemo(() => {
    const nomes = new Set<string>();
    for (const item of todasAsAcoes) {
      if (item.norma) nomes.add(item.norma);
    }
    return [...nomes].sort().map((norma) => ({ value: norma, label: norma }));
  }, [todasAsAcoes]);

  /**
   * A contagem é a deste plano, e é contada aqui porque a rota do plano devolve o cabeçalho sem
   * as ações. O recorte dos filtros muda a tabela, nunca o cabeçalho.
   */
  const vencidas = useMemo(
    () => todasAsAcoes.filter((item) => situacaoDoPrazo(item) === 'VENCIDA').length,
    [todasAsAcoes]
  );

  const abrirNova = useCallback(() => {
    setEmEdicao(null);
    setDialogoDaAcao(true);
  }, []);

  const abrirEdicao = useCallback((item: ActionPlanItem) => {
    setEmEdicao(item);
    setDialogoDaAcao(true);
  }, []);

  const recarregar = useCallback(async () => {
    await carregarPlano();
    await carregarItens();
  }, [carregarItens, carregarPlano]);

  const salvarAcao = useCallback(
    async (dados: DadosDaAcao, evidencia: File | null) => {
      setSalvando(true);
      setErro(null);
      try {
        const salva = emEdicao
          ? await actionPlanService.atualizarItem(id, emEdicao.id, dados)
          : await actionPlanService.criarItem(id, dados);
        if (evidencia) await actionPlanService.enviarEvidencia(id, salva.id, evidencia);
        setDialogoDaAcao(false);
        await recarregar();
      } catch (err: unknown) {
        setErro(err instanceof Error ? err.message : 'Não foi possível salvar a ação');
      } finally {
        setSalvando(false);
      }
    },
    [emEdicao, id, recarregar]
  );

  const trazerDoCatalogo = useCallback(
    async (normas: string[] | null) => {
      setSalvando(true);
      setErro(null);
      try {
        await actionPlanService.trazerDoCatalogo(id, normas);
        setDialogoDoCatalogo(false);
        await recarregar();
      } catch (err: unknown) {
        setErro(err instanceof Error ? err.message : 'Não foi possível trazer o catálogo');
      } finally {
        setSalvando(false);
      }
    },
    [id, recarregar]
  );

  const confirmarExclusao = useCallback(async () => {
    if (!aExcluir) return;
    try {
      await actionPlanService.removerItem(id, aExcluir.id);
      setAExcluir(null);
      await recarregar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível excluir a ação');
    }
  }, [aExcluir, id, recarregar]);

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Plano de Ação', path: '/action-plan' },
        { label: plano?.title ?? 'Plano' },
      ]}
      title={plano?.title ?? 'Plano de ação'}
      subtitle={
        plano
          ? `Elaborado em ${formatarDia(plano.elaboratedAt)} — é desta data que sai o prazo de cada ação`
          : undefined
      }
      actions={
        <>
          <ActionButton
            acao="action_plan.create"
            rotulo="Trazer do catálogo"
            variant="outlined"
            startIcon={<CatalogoIcon />}
            onClick={() => setDialogoDoCatalogo(true)}
            disabled={criar.disabled}
          >
            Trazer do catálogo
          </ActionButton>
          <ActionButton
            acao="action_plan.create"
            rotulo="Nova ação"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirNova}
            disabled={criar.disabled}
          >
            Nova ação
          </ActionButton>
        </>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      {plano && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
          <Chip
            size="small"
            variant="outlined"
            color={plano.status === 'VIGENTE' ? 'success' : 'default'}
            label={ROTULO_DA_SITUACAO_DO_PLANO[plano.status]}
          />
          <Chip size="small" variant="outlined" label={emAcoes(todasAsAcoes.length)} />
          <Chip
            size="small"
            color={vencidas > 0 ? 'error' : 'default'}
            variant={vencidas > 0 ? 'filled' : 'outlined'}
            label={vencidas === 1 ? '1 vencida' : `${vencidas} vencidas`}
          />
        </Stack>
      )}

      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          chips={activeChips}
          onClearAll={clearAll}
          resultCount={itens.length}
          resultNoun="ações"
          loading={carregando}
        >
          <InlineSelect
            label="Situação"
            value={filters.status}
            options={SITUACOES_DA_ACAO.map((s) => ({ value: s, label: ROTULO_DA_SITUACAO[s] }))}
            onChange={(v) => setFilter('status', v)}
            emptyLabel="Todas"
          />
          <InlineSelect
            label="Prioridade"
            value={filters.priority}
            options={PRIORIDADES.map((p) => ({ value: p, label: ROTULO_DA_PRIORIDADE[p] }))}
            onChange={(v) => setFilter('priority', v)}
            emptyLabel="Todas"
          />
          <InlineSelect
            label="Norma"
            value={filters.norma}
            options={normasDoPlano}
            onChange={(v) => setFilter('norma', v)}
            emptyLabel="Todas"
          />
          <InlineSelect
            label="Responsável"
            value={filters.responsible}
            options={RESPONSAVEIS.map((r) => ({ value: r, label: ROTULO_DO_RESPONSAVEL[r] }))}
            onChange={(v) => setFilter('responsible', v)}
            emptyLabel="Todos"
          />
          <InlineToggles
            items={[
              {
                label: 'Vencidas',
                checked: filters.prazo === 'vencidas',
                onChange: (marcado) => setFilter('prazo', marcado ? 'vencidas' : ''),
              },
              {
                label: 'A vencer',
                checked: filters.prazo === 'a_vencer',
                onChange: (marcado) => setFilter('prazo', marcado ? 'a_vencer' : ''),
              },
            ]}
          />
        </InlineFilterBar>
      </Box>

      <DataTable
        columns={colunas}
        rows={itens}
        getRowId={(item) => item.id}
        loading={carregando}
        filtersActive={hasActiveFilters}
        onClearFilters={clearAll}
        onRowClick={abrirEdicao}
        emptyState={{
          title: 'Este plano ainda não tem nenhuma ação',
          description:
            'Comece trazendo as 77 ações do catálogo, distribuídas em 24 normas e prontas para editar — ou escreva a primeira à mão em "Nova ação".',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: abrirEdicao,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (item: ActionPlanItem) => setAExcluir(item),
            ...excluir,
          },
        ]}
      />

      <ActionPlanItemDialog
        aberto={dialogoDaAcao}
        item={emEdicao}
        companyId={plano?.companyId ?? ''}
        salvando={salvando}
        onFechar={() => setDialogoDaAcao(false)}
        onSalvar={(dados, evidencia) => void salvarAcao(dados, evidencia)}
      />

      <CatalogImportDialog
        aberto={dialogoDoCatalogo}
        trazendo={salvando}
        onFechar={() => setDialogoDoCatalogo(false)}
        onTrazer={(normas) => void trazerDoCatalogo(normas)}
      />

      <Dialog open={aExcluir !== null} onClose={() => setAExcluir(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir esta ação?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {aExcluir?.description} — sai do plano junto com a evidência anexada a ela.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAExcluir(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void confirmarExclusao()}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ActionPlanDetailPage;
